import { useCallback, useState } from 'react'
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux'

import {
  groupReaching,
  useLazyGetUidGroupsByProductQuery,
  variantsSourceOf,
} from 'api/canonicalPicker'
import type {
  PickerRow,
  ProductRow,
  UidGroupRow,
} from 'api/canonicalPicker.types'
import {
  CanonicalLevel,
  PickerSliceState,
  picksOf,
  scopeOf,
  selectActiveSelection,
  SourceChoice,
  SourceSelection,
  switchCanonicalSource,
} from 'slices/aiSegmentBuilder/picker'

type RootState = { picker: PickerSliceState }

const isProductRow = (row: PickerRow): row is ProductRow =>
  row.kind === 'product'

const isUidGroupRow = (row: PickerRow): row is UidGroupRow =>
  row.kind === 'uidGroup'

const productsReachedBy = (group: UidGroupRow): string[] =>
  group.productIds?.length ? group.productIds : [group.canonicalId]

/** Each product's groups in turn, once each though two products share one */
const versionsOf = (
  products: ProductRow[],
  groups: UidGroupRow[]
): UidGroupRow[] => {
  const versions = new Map<string, UidGroupRow>()
  products.forEach(product =>
    groups.forEach(group => {
      const reached = productsReachedBy(group)
      if (reached.includes(product.id) && !versions.has(group.id)) {
        versions.set(group.id, groupReaching(group, reached))
      }
    })
  )
  return Array.from(versions.values())
}

/** The picks no group reaches, which a specific list could not carry over */
const productsWithoutVersions = (
  products: ProductRow[],
  groups: UidGroupRow[]
): ProductRow[] =>
  products.filter(
    product =>
      !groups.some(group => productsReachedBy(group).includes(product.id))
  )

/**
 * Moves a level to its specific source, starting it with the UID groups
 * behind each smart pick: those filed under it and those its variants name,
 * every product each spans included, so level 2 never starts over for a
 * product it keeps. A pick with no groups keeps the level on smart grouping,
 * and names that pick.
 */
const useSwitchToSpecific = (
  pickerId: number,
  level: CanonicalLevel,
  choice: SourceChoice | null,
  selection: SourceSelection
) => {
  const dispatch = useDispatch()
  const store = useStore<RootState>()
  // Level 2's variants are read under level 1's picks, as its smart source lists them
  const scope = useSelector(
    (state: RootState) =>
      level === 'level2'
        ? scopeOf(selectActiveSelection(state, pickerId, 'level1'))
        : null,
    shallowEqual
  )
  const [fetchGroups, { isFetching, isError }] =
    useLazyGetUidGroupsByProductQuery()
  const [refused, setRefused] = useState<{
    selection: SourceSelection
    names: string[]
  } | null>(null)

  const switchToSpecific = useCallback(async () => {
    if (!choice) return

    const products = picksOf(selection).filter(isProductRow)
    const groups =
      products.length > 0
        ? (
            await fetchGroups(
              {
                source: choice.specific,
                variantsSource: variantsSourceOf(choice.smart),
                productIds: products.map(product => product.id),
                scope,
              },
              true
            )
          ).data?.rows.filter(isUidGroupRow)
        : []
    if (!groups) return
    // The merchant may have changed these picks, or level 1, while the groups loaded
    if (
      selectActiveSelection(store.getState(), pickerId, level) !== selection
    ) {
      return
    }

    const missing = productsWithoutVersions(products, groups)
    if (missing.length > 0) {
      setRefused({ selection, names: missing.map(product => product.name) })
      return
    }
    dispatch(
      switchCanonicalSource({
        pickerId,
        level,
        source: choice.specific.name,
        rows: versionsOf(products, groups),
      })
    )
  }, [choice, dispatch, fetchGroups, level, pickerId, scope, selection, store])

  return {
    switchToSpecific,
    isSwitching: isFetching,
    switchFailed: isError,
    // Only while the picks are the ones the switch was refused for
    picksWithoutVersions: refused?.selection === selection ? refused.names : [],
  }
}

export default useSwitchToSpecific
